import {Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography, makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {useEffect, useMemo, useState} from 'react';
import {AgeniumQuestionTranscript, getTranscript} from '@/APIs/Agenium/api';
import {LoadingSpinner} from '@/components/ui/loading-spinner';
import {buildAgeniumReviewQuestions} from './InstantAppModalPages/common/ageniumReviewQuestions';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(1),
    },
    closeButton: {
        color: theme.palette.grey[500],
    },
    questionsContainer: {
        fontFamily: 'FredokaRegular',
        fontSize: 13,
        color: '#616161',
        border: '1px solid #C5CDF8',
        margin: 0,
        height: '45vh',
        overflowY: 'auto',
        resize: 'vertical',
        padding: '2px 5px',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#eee',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: 15,
            padding: '6px 12px',
        },
    },
    questionRow: {
        minHeight: '2.5em',
        alignItems: 'flex-start',
        '&:not(:last-child)': {
            borderBottom: '1px solid #C5CDF8',
        },
    },
    questionCell: {
        color: '#456d69',
        padding: '8px 4px',
        paddingRight: '8px',
        display: 'flex',
        alignItems: 'flex-start',
        wordBreak: 'break-word',
        hyphens: 'auto',
        '& *': {
            hyphens: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            padding: '10px 12px',
        },
    },
    answerCell: {
        color: '#333',
        padding: '8px 4px',
        paddingLeft: '8px',
        wordBreak: 'break-word',
        hyphens: 'auto',
        '& *': {
            hyphens: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            padding: '10px 12px',
        },
    },
    emptyState: {
        marginTop: theme.spacing(2),
        color: '#616161',
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '45vh',
        gap: theme.spacing(1),
    },
}));

interface AgeniumReviewDialogProps {
    open: boolean;
    onClose: () => void;
    applicationId?: string | null;
}

const AgeniumReviewDialog = ({open, onClose, applicationId}: AgeniumReviewDialogProps) => {
    const classes = useStyles();
    const [transcript, setTranscript] = useState<AgeniumQuestionTranscript[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const filteredQuestions = useMemo(() => buildAgeniumReviewQuestions(transcript), [transcript]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!applicationId) {
            setTranscript([]);
            setError('Agenium Case ID not found');
            return;
        }
        setLoading(true);
        getTranscript(applicationId)
            .then((data) => {
                setTranscript(data);
                setError('');
            })
            .catch(() => {
                setTranscript([]);
                setError('Failed to load transcript');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [open, applicationId]);

    const purifyHtml = (html: string) =>
        DOMPurify.sanitize(html, {
            USE_PROFILES: {html: true},
        });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="agenium-review-dialog-title">
            <DialogTitle id="agenium-review-dialog-title" disableTypography className={classes.dialogTitle}>
                <Typography variant="h6">Review instant app answers</Typography>
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <div className={classes.loadingWrapper}>
                        <LoadingSpinner className="w-8 h-8" />
                        <Typography variant="body2">Loading...</Typography>
                    </div>
                ) : error ? (
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                ) : filteredQuestions.length ? (
                    <Grid id="reviewSummaryQuestions" container className={classes.questionsContainer}>
                        {filteredQuestions.map((item, index) => (
                            <Grid item container xs={12} className={classes.questionRow} key={`${item.id}-${index}`}>
                                <Grid item xs={12} sm={7} className={classes.questionCell} lang="en">
                                    {parse(purifyHtml(item.question))}
                                </Grid>
                                <Grid item xs={12} sm={5} className={classes.answerCell} lang="en">
                                    {parse(purifyHtml(item.answer ?? ''))}
                                    {item.answer2 && (
                                        <>
                                            <br />
                                            {parse(purifyHtml(item.answer2))}
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Typography variant="body2" className={classes.emptyState}>
                        No answers to display yet.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AgeniumReviewDialog;
